import { useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  GENERATION_TOOL,
  GENERATION_TOOL_CARD_NEW_DOCUMENT,
  getMultiPromptSubtools,
  getToolCopy,
  getToolDescription,
  getToolTitle,
  isMultiPromptTool,
  toolHasDescriptionInTitle,
} from 'src/data/generationTool';
import { TEMPLATE_BUILDER_PARAMS, TEMPLATE_STATUS } from 'src/data/templates';

import { CardCondensed } from './components/CardCondensed';
import { CardLarge } from './components/CardLarge';
import { CardMedium } from './components/CardMedium';
import { CardMinimized } from './components/CardMinimized';
import { CardSmall } from './components/CardSmall';

export const GENERATION_TOOL_CARD_TYPE = {
  medium: 'medium',
  large: 'large',
  small: 'small',
  minimized: 'minimized',
  condensed: 'condensed',
};

const CARD_COMPONENT = {
  [GENERATION_TOOL_CARD_TYPE.medium]: CardMedium,
  [GENERATION_TOOL_CARD_TYPE.large]: CardLarge,
  [GENERATION_TOOL_CARD_TYPE.small]: CardSmall,
  [GENERATION_TOOL_CARD_TYPE.minimized]: CardMinimized,
  [GENERATION_TOOL_CARD_TYPE.condensed]: CardCondensed,
};

export const GenerationToolCard = ({
  className,
  classes,
  type,
  generationTool,
  customToolData,
  selected,
  onSelect,
  disabled,
  hideToolIcons,
  hideMultiPromptTag,
  showComingSoonTag,
}) => {
  const multiPromptTag = customToolData
    ? customToolData.multiPromptTag
    : isMultiPromptTool(generationTool);
  const value = { tool: generationTool };
  const isCsvTemplate = customToolData?.templateType === TEMPLATE_BUILDER_PARAMS.csv;

  const title = useMemo(() => {
    switch (generationTool) {
      case GENERATION_TOOL_CARD_NEW_DOCUMENT:
        return 'Blank';
      case GENERATION_TOOL.customMultiPrompt:
        return customToolData.name;
      default:
        return getToolTitle(generationTool);
    }
  }, [generationTool, customToolData]);

  const description = useMemo(() => {
    // Some tools has description partially in the title already
    if (toolHasDescriptionInTitle(generationTool)) {
      return '';
    }

    return getToolDescription(generationTool);
  }, [generationTool]);

  const copy = useMemo(() => {
    if (generationTool === GENERATION_TOOL.customMultiPrompt) {
      return customToolData.info ? customToolData.info : customToolData.description;
    } else {
      return getToolCopy(generationTool);
    }
  }, [generationTool, customToolData]);

  const toolList = useMemo(() => {
    if (generationTool === GENERATION_TOOL.customMultiPrompt) {
      return customToolData.subTools;
    } else if (isMultiPromptTool(generationTool)) {
      return [...new Set(Object.values(getMultiPromptSubtools(generationTool)))];
    }
    return [generationTool];
  }, [generationTool, customToolData]);

  const CardComponent = CARD_COMPONENT[type] || CardSmall;
  return (
    <CardComponent
      className={className}
      classes={classes}
      value={value}
      title={title}
      description={description}
      copy={copy}
      toolList={toolList}
      customMultiPromptId={customToolData?.id}
      customMultiPromptGroup={
        customToolData?.status === TEMPLATE_STATUS.released
          ? customToolData?.templateGroup
          : undefined
      }
      customMultiPromptProjectType={customToolData?.projectType}
      showMultiPromptTag={(multiPromptTag || isCsvTemplate) && !hideMultiPromptTag}
      selected={selected}
      onSelect={onSelect}
      disabled={disabled}
      condensedImage={customToolData?.smallImage}
      largeImage={customToolData?.bigImage}
      wideImage={customToolData?.wideImage}
      isCsvTemplate={isCsvTemplate}
      hideToolIcons={hideToolIcons}
      showComingSoonTag={showComingSoonTag}
    />
  );
};

GenerationToolCard.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  type: PropTypes.oneOf(Object.values(GENERATION_TOOL_CARD_TYPE)),
  generationTool: PropTypes.oneOf([
    ...Object.values(GENERATION_TOOL),
    GENERATION_TOOL_CARD_NEW_DOCUMENT,
  ]).isRequired,
  customToolData: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    subTools: PropTypes.array,
    id: PropTypes.string,
    multiPromptTag: PropTypes.bool,
    smallImage: PropTypes.string,
    bigImage: PropTypes.string,
    wideImage: PropTypes.string,
    templateGroup: PropTypes.string,
    status: PropTypes.string,
    info: PropTypes.string,
    templateType: PropTypes.string,
    projectType: PropTypes.string,
  }),
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  hideToolIcons: PropTypes.bool,
  hideMultiPromptTag: PropTypes.bool,
  showComingSoonTag: PropTypes.bool,
};

GenerationToolCard.defaultProps = {
  className: null,
  classes: {},
  type: GENERATION_TOOL_CARD_TYPE.small,
  customToolData: null,
  selected: false,
  disabled: false,
  hideToolIcons: false,
  hideMultiPromptTag: false,
  showComingSoonTag: false,
};
